import React from 'react';
import moment from 'moment';
import { Avatar } from '@mui/material';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import LocalPhoneTwoToneIcon from '@mui/icons-material/LocalPhoneTwoTone';
import LocationOnTwoToneIcon from '@mui/icons-material/LocationOnTwoTone';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EuroSymbolSharpIcon from '@mui/icons-material/EuroSymbolSharp';

import { capitalizeFirstLetter, formatPhoneNumber } from '../../Utils/Function';
import Functions from '../../Helpers/Functions';
import { useTailwindBrand } from '../../Components/shared';
import Teachr from '../../Objects/models/Teachr';
import { getActiveClientBrandConfig, getActivePrestataireBrandConfig, getActivePrestationBrandConfig } from '../../config/environment.config';
import { MAX_COURSES_PER_MONTH, describeMonthlyQuota, monthlyQuotaOf } from '../../Utils/monthlyQuota';
import { AccountStatusType } from '../../Enum/AccountStatus';
import { StatusBadge } from './StatusBadge';
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer, vtTtcFromNet } from '../../Utils/VerifiedTrainerAmounts';
import { ClientStatusBanner, StatusBannerContext } from './ClientStatusBanner';
import { resolveClientRowFields } from './clientDetailNavigation';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StudentInfoCardProps {
  /** Ligne client au format de la liste (all_clients). */
  client: any;
  teachr?: Teachr;
  /** Envoi de l'e-mail de relance depuis la bannière de statut. */
  onRelance?: () => void;
  /**
   * Chiffres du mois (heures, montants, quota de cours tous prestataires).
   * La fiche dédiée les masque : elle a ses propres statistiques, limitées
   * aux cours du professeur connecté.
   */
  showActivity?: boolean;
  bannerContext?: StatusBannerContext;
  /** Contenu ajouté sous le nom (actions de la fiche par exemple). */
  headerExtra?: React.ReactNode;
  /** Faits clés affichés en pastilles sous le statut (fiche : client depuis, dernier cours...). */
  facts?: Array<{ icon: React.ReactNode; label: string; value: string }>;
}

// ─── Composant ────────────────────────────────────────────────────────────────

/** Carte d'identité du client : en-tête, coordonnées, activité du mois et bannière de statut. */
const StudentInfoCard: React.FC<StudentInfoCardProps> = ({
  client,
  teachr,
  onRelance,
  showActivity = true,
  bannerContext = 'row',
  headerExtra,
  facts,
}) => {
  const { classes, brandConfig, common } = useTailwindBrand();
  const { fullName, initFullName, dateTime, codeTdp, descTdp, isArchive } = resolveClientRowFields(client);

  const duration = client?.duration === 0 ? '0h' : (Functions.renderDuration(client?.duration) || '-');
  // Verified Trainer : prix total TTC facturé ce mois (l'API renvoie un net)
  const amountDisplay = client?.amount != null
    ? `${(isVerifiedTrainer() ? vtTtcFromNet(client.amount, teachr) : parseFloat(client.amount)).toFixed(2)} €`
    : '-';

  // Nom de l'élève (Teachr : payeur ≠ élève)
  const childFirst = capitalizeFirstLetter(client?.childFirstName ?? client?.child_first_name ?? '');
  const childLast = (client?.childLastName ?? client?.child_last_name ?? '').toUpperCase();
  const studentName = (childFirst || childLast) ? `${childFirst} ${childLast}`.trim() : null;
  const showStudent = studentName && studentName !== fullName.trim();

  // Date d'activation extraite de la description TDP
  const activationDate = dateTime
    ? dateTime.format('DD MMM YYYY [à] HH[h]mm')
    : null;

  type ContactRow = { icon: React.ReactNode; label: string; value: string; href?: string };
  const contactRows: ContactRow[] = [
    client?.mail
      ? { icon: <EmailTwoToneIcon fontSize="small" />, label: 'Email', value: client.mail, href: `mailto:${client.mail}` }
      : null,
    client?.phone
      ? { icon: <LocalPhoneTwoToneIcon fontSize="small" />, label: 'Téléphone', value: formatPhoneNumber(client?.phone), href: `tel:${client.phone}` }
      : null,
    client?.address
      ? { icon: <LocationOnTwoToneIcon fontSize="small" />, label: 'Adresse', value: client.address }
      : null,
    client?.birthday
      ? {
          icon: <CakeOutlinedIcon fontSize="small" />,
          label: 'Naissance',
          value: `${moment(client.birthday).format('DD/MM/YYYY')}${client.age != null ? ` (${client.age} ans)` : ''}`,
        }
      : null,
    // la fiche détaille l'activation dans « Avance immédiate » : pas de doublon ici
    activationDate && bannerContext !== 'page'
      ? { icon: <AccessTimeIcon fontSize="small" />, label: 'Activation', value: activationDate }
      : null,
  ].filter(Boolean) as ContactRow[];

  return (
    <article className={`bg-white rounded-xl overflow-hidden shadow-sm ring-1 ring-gray-100 ${common.body}`}>

      {/* ── Header : dégradé aux couleurs de la marque (Teach'r, Verified Trainer, Déclarons) ── */}
      <div
        aria-hidden
        style={{ height: 5, background: `linear-gradient(90deg, ${brandConfig.colors.primary}, ${brandConfig.colors.secondary})` }}
      />
      <div
        className="px-6 pt-6 pb-5 border-b border-gray-100"
        style={{ background: `linear-gradient(135deg, ${brandConfig.colors.primary}12 0%, ${brandConfig.colors.secondary}1a 100%)` }}
      >
        <div className="flex items-start gap-5">
          {/* Avatar */}
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: brandConfig.colors.primary,
              fontSize: '1.6rem',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              border: '3px solid white',
              flexShrink: 0,
            }}
          >
            {initFullName}
          </Avatar>

          {/* Identité */}
          <div className="flex-1 min-w-0 pt-1">
            <h2 className="font-bold text-lg leading-tight text-gray-900 mb-0.5 truncate">
              {fullName}
            </h2>
            {showStudent && (
              <p className="text-sm text-gray-500 mb-2">
                🎓 <span className="font-medium text-gray-700">{studentName}</span>
              </p>
            )}

            {/* Badge statut */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {codeTdp && (
                <StatusBadge id={client?.id} status={codeTdp as AccountStatusType} description={descTdp ?? ''} />
              )}
              {isArchive && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                  Archivé
                </span>
              )}
            </div>

            {/* Faits clés de la fiche */}
            {facts && facts.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {facts.map((fact) => (
                  <span
                    key={fact.label}
                    className="inline-flex items-center gap-1.5 bg-white/80 border border-white/60 px-2.5 py-1 rounded-full text-xs font-medium text-gray-700 shadow-sm"
                    title={fact.label}
                  >
                    <span className={`${classes.primaryText} flex`}>{fact.icon}</span>
                    <span className="text-gray-400">{fact.label}</span>
                    <span className="font-semibold">{fact.value}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Stats (uniquement pour les clients validés) */}
            {showActivity && client?.isValidated && (
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 bg-white/80 border border-white/60 px-2.5 py-1 rounded-full text-xs font-medium text-gray-700 shadow-sm">
                  <ScheduleIcon sx={{ fontSize: 13 }} className={classes.primaryText} />
                  <span className="font-semibold">{duration}</span>
                  <span className="text-gray-400">dispensées</span>
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/80 border border-white/60 px-2.5 py-1 rounded-full text-xs font-medium text-gray-700 shadow-sm">
                  <EuroSymbolSharpIcon sx={{ fontSize: 13 }} className={classes.primaryText} />
                  <span className="font-semibold">{amountDisplay}</span>
                  <span className="text-gray-400">facturés</span>
                </span>
              </div>
            )}

            {headerExtra}
          </div>
        </div>
      </div>

      {/* ── Infos de contact ── */}
      {contactRows.length > 0 && (
        <div className="divide-y divide-gray-50">
          {contactRows.map((row, i) => (
            <div key={i} className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50/70 transition-colors">
              <span className={`${classes.primaryText} shrink-0`}>{row.icon}</span>
              <span className="text-xs text-gray-400 w-20 shrink-0">{row.label}</span>
              {row.href ? (
                <a href={row.href} className="text-sm font-medium text-gray-800 hover:underline decoration-dotted break-words min-w-0">
                  {row.value}
                </a>
              ) : (
                <span className="text-sm font-medium text-gray-700 break-words min-w-0">{row.value}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Activité du mois en cours (montant déclaré + cours + plafonds) ── */}
      {showActivity && client?.isValidated && (() => {
        const defaultFees = parseFloat(teachr?.defaultFees ?? '0') || 0;
        const netMultiplier = 1 - defaultFees;
        // Verified Trainer : plafond et montant déclaré en prix total TTC (l'API renvoie un net)
        const isVt = isVerifiedTrainer();
        const maxNet = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier;
        const currentMonthName = capitalizeFirstLetter(moment().locale('fr').format('MMMM'));

        const prestationSingular = getActivePrestationBrandConfig();
        const prestationPlural = getActivePrestationBrandConfig('s');
        const clientWord = getActiveClientBrandConfig();

        // Montant déclaré ce mois vs plafond mensuel (1980 € TTC chez Verified Trainer, 1980 € net ailleurs)
        const amountDeclared = client?.amountForTheMonth != null
          ? (isVt ? vtTtcFromNet(client.amountForTheMonth, teachr) : parseFloat(client.amountForTheMonth))
          : 0;
        const amountRemaining = Math.max(0, maxNet - amountDeclared);
        const amountReached = amountRemaining <= 0;

        // Cours pris ce mois tous prestataires confondus vs plafond de 8
        const quota = monthlyQuotaOf(client?.coursesThisRollingMonth ?? 0, client?.coursesMonthByThisTeachr)!;
        const quotaText = describeMonthlyQuota(quota);
        const coursesByThisTeachr = quota.mine;
        const coursesByOthers = quota.others;
        const coursesReached = quota.reached;

        return (
          <div className="px-5 py-4 border-t border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Activité de {currentMonthName}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {/* Montant déclaré ce mois */}
              <div className={`rounded-lg border p-3 ${amountReached ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                  <EuroSymbolSharpIcon sx={{ fontSize: 13 }} />
                  <span>Montant déclaré</span>
                </div>
                <p className={`text-lg font-bold leading-none ${amountReached ? 'text-red-600' : 'text-gray-900'}`}>
                  {amountDeclared.toFixed(2)} €
                </p>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Plafond : {maxNet.toFixed(0)} € / mois
                </p>
                <p className={`text-[11px] mt-0.5 font-medium ${amountReached ? 'text-red-600' : 'text-gray-500'}`}>
                  {amountReached ? 'Plafond atteint ce mois' : `${amountRemaining.toFixed(0)} € restants ce mois`}
                </p>
              </div>

              {/* Cours pris ce mois */}
              <div className={`rounded-lg border p-3 ${coursesReached ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                  <ScheduleIcon sx={{ fontSize: 13 }} />
                  <span>{capitalizeFirstLetter(prestationPlural)} pris</span>
                </div>
                <p className={`text-lg font-bold leading-none ${coursesReached ? 'text-red-600' : quota.nearLimit ? 'text-amber-600' : 'text-gray-900'}`}>
                  {quotaText.headline}
                </p>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Plafond : {MAX_COURSES_PER_MONTH} {prestationPlural} / mois, tous {getActivePrestataireBrandConfig("s")} confondus
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {quotaText.detail}
                </p>
              </div>
            </div>

            {/* Message : quota mensuel de cours atteint */}
            {coursesReached && (
              <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <span className="shrink-0 leading-snug">⚠️</span>
                <p>
                  {capitalizeFirstLetter(clientWord)} a déjà utilisé tout son quota de {prestationSingular} ce mois
                  {coursesByOthers > 0 && (
                    <> (dont <strong>{coursesByOthers}</strong> avec un ou plusieurs autres professeurs)</>
                  )}.
                  {coursesByThisTeachr < 8 && " Si ce n'est pas avec vous, c'est avec un ou plusieurs autres professeurs."}
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Bannière contextuelle selon le statut (la fiche l'affiche dans « Avance immédiate ») ── */}
      {bannerContext !== 'page' && (
        <ClientStatusBanner
          status={client?.status}
          description={client?.description}
          onRelance={onRelance}
          context={bannerContext}
        />
      )}
    </article>
  );
};

export default StudentInfoCard;
